import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
    Box, Typography, TextField, Table, TableHead, TableRow, TableCell, TableBody, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, MenuItem, TablePagination
} from "@mui/material";
import { Visibility, Edit, Delete, SwapHoriz, Build } from "@mui/icons-material";
import {
    collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp, onSnapshot, query, where, orderBy
} from "firebase/firestore";
import { db } from "../../firebase";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";

function Customeracc() {
    const navigate = useNavigate();
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
    });

    const [customers, setCustomers] = useState([]);
    const [trips, setTrips] = useState([]);
    const [payments, setPayments] = useState([]);
    const [payModes, setPayModes] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const rowsPerPage = 10;
    const [openHistory, setOpenHistory] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerPayments, setCustomerPayments] = useState([]);
    const [openPayment, setOpenPayment] = useState(false);
    const [editPaymentId, setEditPaymentId] = useState(null);

    const [openTransfer, setOpenTransfer] = useState(false);
    const [transferFrom, setTransferFrom] = useState(null);
    const [transferToId, setTransferToId] = useState("");
    const [isTransferring, setIsTransferring] = useState(false);

    const [openCustEdit, setOpenCustEdit] = useState(false);
    const [custForm, setCustForm] = useState({ customerName: "", mobile: "" });
    const [editingCustId, setEditingCustId] = useState(null);

    const [paymentForm, setPaymentForm] = useState({ amount: "", paymentMode: "", reference: "", note: "", date: "" });

    // Load Data with Real-time Listeners
    useEffect(() => {
        const unsubCustomers = onSnapshot(query(collection(db, "customers"), orderBy("customerName", "asc")), (snap) => {
            setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        const unsubTrips = onSnapshot(collection(db, "trips"), snap => {
            setTrips(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        // Fetch payments ordered by date desc to maintain consistent history sorting instantly
        const unsubPayments = onSnapshot(query(collection(db, "customer_history"), orderBy("date", "desc")), (snap) => {
            const allPayments = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setPayments(allPayments);

            // If history modal is open, dynamically keep customer specific payments updated smoothly
            if (selectedCustomer) {
                const history = allPayments.filter(p => p.customerId === selectedCustomer.id);
                setCustomerPayments(history);
            }
        });

        const unsubPayModes = onSnapshot(collection(db, "payment_modes"), snap => {
            setPayModes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => {
            unsubCustomers();
            unsubTrips();
            unsubPayments();
            unsubPayModes();
        };
    }, [selectedCustomer?.id]);

    // Memoized Lookups & Aggregations to prevent layout lag & stuttering
    const customerMetrics = useMemo(() => {
        const metrics = {};

        // Trips calculations
        trips.forEach(trip => {
            if (!trip.customerId) return;
            if (!metrics[trip.customerId]) {
                metrics[trip.customerId] = { totalSale: 0, totalPaid: 0, tripPaid: 0, totalTrips: 0 };
            }
            metrics[trip.customerId].totalTrips += 1;
            const tripTotal = trip.products?.reduce((s, p) => s + Number(p.amount || 0), 0) || 0;
            metrics[trip.customerId].totalSale += tripTotal;
            metrics[trip.customerId].tripPaid += Number(trip.paidAmount || 0);
        });

        // Payments calculations
        payments.forEach(payment => {
            if (!payment.customerId) return;
            if (!metrics[payment.customerId]) {
                metrics[payment.customerId] = { totalSale: 0, totalPaid: 0, tripPaid: 0, totalTrips: 0 };
            }
            metrics[payment.customerId].totalPaid += Number(payment.amount || 0);
        });

        return metrics;
    }, [trips, payments]);

    const getCustomerTotalSale = useCallback((id) => customerMetrics[id]?.totalSale || 0, [customerMetrics]);
    const getCustomerTotalPaid = useCallback((id) => customerMetrics[id]?.totalPaid || 0, [customerMetrics]);
    const getCustomerTripsPaid = useCallback((id) => customerMetrics[id]?.tripPaid || 0, [customerMetrics]);
    const getCustomerTripsCount = useCallback((id) => customerMetrics[id]?.totalTrips || 0, [customerMetrics]);

    const getPaymentMode = useCallback((id) => {
        const mode = payModes.find(m => m.payMode === id);
        return mode ? mode.payMode : id;
    }, [payModes]);

    const filtered = useMemo(() => {
        return customers.filter(c => {
            const searchTerm = search.toLowerCase();
            return (
                c.customerName?.toLowerCase().includes(searchTerm) ||
                c.mobile?.toLowerCase().includes(searchTerm)
            );
        });
    }, [customers, search]);

    const handleView = (customer) => {
        const history = payments.filter(p => p.customerId === customer.id);
        setSelectedCustomer(customer);
        setCustomerPayments(history);
        setOpenHistory(true);
    };

    const adjustTripsPayment = async (customerId, paidAmount, paymentMode) => {
        try {
            const q = query(collection(db, "trips"), where("customerId", "==", customerId), where("remainingAmount", ">", 0), orderBy("tareDate", "asc"));
            const snapshot = await getDocs(q);
            if (snapshot.empty) return;

            let remainingPayment = paidAmount;
            for (const tripDoc of snapshot.docs) {
                if (remainingPayment <= 0) break;
                const trip = tripDoc.data();
                const tripId = tripDoc.id;
                const tripRemaining = Number(trip.remainingAmount || 0);
                const alreadyPaid = Number(trip.paidAmount || 0);
                let payNow = Math.min(remainingPayment, tripRemaining);
                const newPaid = alreadyPaid + payNow;
                const newRemaining = tripRemaining - payNow;
                const oldPayments = trip.payments || [];
                const newPaymentEntry = {
                    amount: payNow,
                    mode: paymentMode,
                    date: Timestamp.now()
                };
                await updateDoc(doc(db, "trips", tripId), {
                    paidAmount: newPaid,
                    remainingAmount: newRemaining,
                    payments: [...oldPayments, newPaymentEntry],
                    lastPaymentDate: Timestamp.now()
                });
                remainingPayment -= payNow;
            }
        } catch (err) {
            console.error("Error updating trips:", err);
        }
    };

    const handleSavePayment = async () => {
        const data = {
            ...paymentForm,
            amount: Number(paymentForm.amount),
            customerId: selectedCustomer.id,
            date: Timestamp.fromDate(new Date(paymentForm.date)),
            type: "Account",
            entryTime: Timestamp.now(),
        };

        if (editPaymentId) {
            await updateDoc(doc(db, "customer_history", editPaymentId), data);
        } else {
            await addDoc(collection(db, "customer_history"), data);
        }

        setOpenPayment(false);
        await adjustTripsPayment(selectedCustomer.id, Number(paymentForm.amount), paymentForm.paymentMode);

        const totalSale = getCustomerTotalSale(selectedCustomer.id);
        const totalPaid = getCustomerTotalPaid(selectedCustomer.id) + Number(paymentForm.amount);
        const remaining = totalSale - totalPaid;
        const paymentModeName = getPaymentMode(paymentForm.paymentMode);
        const formattedDate = moment(paymentForm.date).format("DD/MM/YYYY");

        if (selectedCustomer.mobile) {
            await sendPaymentWhatsApp(selectedCustomer.mobile, selectedCustomer.customerName, paymentForm.amount, formattedDate, paymentModeName, remaining);
        }
    };

    const reverseTripsPayment = async (customerId, paidAmount) => {
        try {
            const q = query(collection(db, "trips"), where("customerId", "==", customerId), orderBy("tareDate", "desc"));
            const snapshot = await getDocs(q);
            let remaining = Number(paidAmount);

            for (const tripDoc of snapshot.docs) {
                if (remaining <= 0) break;
                const trip = tripDoc.data();
                let paid = Number(trip.paidAmount || 0);
                let balance = Number(trip.remainingAmount || 0);
                let tripPayments = [...(trip.payments || [])];

                while (tripPayments.length && remaining > 0) {
                    const lastPayment = tripPayments[tripPayments.length - 1];
                    const paymentAmount = Number(lastPayment.amount || 0);

                    if (paymentAmount <= remaining) {
                        tripPayments.pop();
                        paid -= paymentAmount;
                        balance += paymentAmount;
                        remaining -= paymentAmount;
                    } else {
                        lastPayment.amount = paymentAmount - remaining;
                        tripPayments[tripPayments.length - 1] = lastPayment;
                        paid -= remaining;
                        balance += remaining;
                        remaining = 0;
                    }
                }

                await updateDoc(doc(db, "trips", tripDoc.id), {
                    paidAmount: paid,
                    remainingAmount: balance,
                    payments: tripPayments
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeletePayment = async (id) => {
        const payment = customerPayments.find(p => p.id === id);
        if (!payment) return;

        await reverseTripsPayment(payment.customerId, Number(payment.amount));
        await deleteDoc(doc(db, "customer_history", id));

        Toast.fire({
            icon: "success",
            title: "Payment deleted successfully"
        });
    };

    const formatMoney = (n) => `₹ ${Number(n || 0).toLocaleString()}`;

    const sendPaymentWhatsApp = async (mobile, customerName, amount, date, paymentModeName, remaining) => {
        try {
            const phoneNumberId = "1092943420558604";
            const accesstoken = "EAAKj5T0Mx1YBRNd6VhhufPP2ApIjZCsZBFOkl855np9VZBvPvZAZAj7axVAevZBiapxd1Iw38v0hhZBwB2knNeLYSVgjR3RqtPn164H2GQ3ljP46kQvAAq0gsl3lfKDh55ZCsmo5fGZCaUSPpCRqeFjVA6VUeIaiu0ZCNxPPMTsV4oxnnUz4TRoICdbiW0xsAncwZDZD";

            const cleanMobile = mobile.replace(/\D/g, '');
            const formattedMobile = cleanMobile.length === 10 ? "91" + cleanMobile : cleanMobile;

            const payload = {
                messaging_product: "whatsapp",
                to: formattedMobile,
                type: "template",
                template: {
                    name: "customer_pay",
                    language: { code: "en" },
                    components: [
                        {
                            type: "body",
                            parameters: [
                                { type: "text", text: customerName ? String(customerName) : "Customer" },
                                { type: "text", text: amount ? String(amount) : "0" },
                                { type: "text", text: date ? String(date) : "Recent" },
                                { type: "text", text: paymentModeName ? String(paymentModeName) : "Account" },
                                { type: "text", text: remaining ? String(remaining) : "0" }
                            ]
                        },
                        {
                            type: "button",
                            sub_type: "url",
                            index: "0",
                            parameters: [
                                { type: "text", text: "https://matoshriinfradharashiv.co.in/" }
                            ]
                        }
                    ]
                }
            };

            const res = await fetch(`https://graph.facebook.com/v22.0/${phoneNumberId}/messages`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accesstoken}`
                },
                body: JSON.stringify(payload)
            });

            const responseData = await res.json();

            if (res.ok) {
                Toast.fire({ icon: "success", title: "WhatsApp Sent" });
            } else {
                console.error("WhatsApp API Error:", responseData);
                Swal.fire("WhatsApp Error", responseData.error?.message || "Check console for details", "error");
            }
        } catch (err) {
            console.error("WhatsApp Exception", err);
        }
    };

    const handleTransferHistory = async () => {
        if (!transferToId) {
            return Swal.fire("Error", "Please select a target customer", "error");
        }

        const targetCustomer = customers.find(c => c.id === transferToId);
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `This will move ALL trips and payments from "${transferFrom.customerName}" to "${targetCustomer.customerName}".`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, transfer everything!'
        });

        if (!result.isConfirmed) return;

        setIsTransferring(true);
        try {
            const tripsSnap = await getDocs(query(collection(db, "trips"), where("customerId", "==", transferFrom.id)));
            const paySnap = await getDocs(query(collection(db, "customer_history"), where("customerId", "==", transferFrom.id)));
            const promises = [];

            tripsSnap.docs.forEach(tripDoc => {
                promises.push(updateDoc(doc(db, "trips", tripDoc.id), {
                    customerId: targetCustomer.id,
                    customerName: targetCustomer.customerName
                }));
            });
            paySnap.docs.forEach(payDoc => {
                promises.push(updateDoc(doc(db, "customer_history", payDoc.id), {
                    customerId: targetCustomer.id
                }));
            });

            await Promise.all(promises);
            setOpenTransfer(false);
            setTransferToId("");
            Toast.fire({ icon: "success", title: "History Transferred Successfully" });
        } catch (error) {
            Swal.fire("Error", "Failed to transfer data", "error");
        } finally {
            setIsTransferring(false);
        }
    };

    const handleUpdateCustomer = async () => {
        try {
            await updateDoc(doc(db, "customers", editingCustId), custForm);
            setOpenCustEdit(false);
            Toast.fire({ icon: "success", title: "Customer updated successfully" });
        } catch (error) {
            Swal.fire("Error", "Failed to update customer", "error");
        }
    };

    const handleDeleteCustomer = async (customer) => {
        const totalSale = getCustomerTotalSale(customer.id);
        const totalPaid = getCustomerTotalPaid(customer.id);
        const remaining = totalSale - totalPaid;

        const result = await Swal.fire({
            title: 'Delete Customer?',
            html: `<b>Customer:</b> ${customer.customerName}<br/><b>Remaining:</b> ₹${remaining.toLocaleString()}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes Delete'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, "customers", customer.id));
                Toast.fire({ icon: "success", title: "Customer deleted" });
            } catch (error) {
                Swal.fire("Error", "Could not delete customer", "error");
            }
        }
    };

    const repairCustomer = async (customer) => {
        try {
            const result = await Swal.fire({
                title: "Repair Customer?",
                text: `Recalculate all trip payments for ${customer.customerName}?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Repair"
            });

            if (!result.isConfirmed) return;

            const tripSnap = await getDocs(query(collection(db, "trips"), where("customerId", "==", customer.id), orderBy("tareDate", "asc")));

            for (const tripDoc of tripSnap.docs) {
                const trip = tripDoc.data();
                const totalAmount = (trip.products || []).reduce((sum, p) => sum + Number(p.amount || 0), 0);
                await updateDoc(doc(db, "trips", tripDoc.id), {
                    paidAmount: 0,
                    remainingAmount: totalAmount,
                    payments: [],
                    lastPaymentDate: null
                });
            }

            const paymentSnap = await getDocs(query(collection(db, "customer_history"), where("customerId", "==", customer.id), orderBy("date", "asc")));

            for (const paymentDoc of paymentSnap.docs) {
                const payment = paymentDoc.data();
                await adjustTripsPayment(customer.id, Number(payment.amount), payment.paymentMode);
            }

            Toast.fire({ icon: "success", title: `${customer.customerName} repaired successfully` });
        } catch (error) {
            Swal.fire("Error", "Repair failed", "error");
        }
    };

    return (
        <Box p={2}>
            <Typography variant="h5" fontWeight="bold" mb={2}>Customer Accounts</Typography>
            <TextField size="small" placeholder="Search Customer" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 2 }} />
            <Paper>
                <Table size="small">
                    <TableHead sx={{ background: "#f5f5f5" }}>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Customer Details</TableCell>
                            <TableCell align="center">Trips</TableCell>
                            <TableCell align="right">Total Sale</TableCell>
                            <TableCell align="right">Total Paid</TableCell>
                            <TableCell align="right">Trips Paid</TableCell>
                            <TableCell align="right">Remaining</TableCell>
                            <TableCell align="center">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((customer, index) => {
                                const totalSale = getCustomerTotalSale(customer.id);
                                const totalPaid = getCustomerTotalPaid(customer.id);
                                const remaining = totalSale - totalPaid;
                                const totalTrips = getCustomerTripsCount(customer.id);
                                const tripPaid = getCustomerTripsPaid(customer.id);
                                const isMismatch = Number(totalPaid.toFixed(2)) !== Number(tripPaid.toFixed(2));

                                return (
                                    <TableRow key={customer.id} hover>
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">{customer.customerName}</Typography>
                                            <Typography variant="caption" color="text.secondary">{customer.mobile || "No Mobile"}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2" fontWeight="bold" color="primary" onClick={() => navigate(`/customer-trip-history/${customer.id}`)} sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                                                {totalTrips}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right"><Typography fontWeight="600">{formatMoney(totalSale)}</Typography></TableCell>
                                        <TableCell align="right"><Typography fontWeight="600" color="success.main">{formatMoney(totalPaid)}</Typography></TableCell>
                                        <TableCell align="right"><Typography fontWeight="600" color={tripPaid === totalPaid ? "success.main" : "error.main"}>{formatMoney(tripPaid)}</Typography></TableCell>
                                        <TableCell align="right">
                                            <Typography fontWeight="bold" color={remaining > 0 ? "error.main" : "success.main"}>
                                                {formatMoney(remaining)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                                                <IconButton color="primary" onClick={() => handleView(customer)} title="View Payment History"><Visibility /></IconButton>
                                                <IconButton color="info" onClick={() => { setEditingCustId(customer.id); setCustForm({ customerName: customer.customerName || "", mobile: customer.mobile || "" }); setOpenCustEdit(true); }} title="Edit Customer"><Edit /></IconButton>
                                                <IconButton color="error" onClick={() => handleDeleteCustomer(customer)} title="Delete Customer"><Delete /></IconButton>
                                                <IconButton color="warning" onClick={() => { setTransferFrom(customer); setOpenTransfer(true); }} title="Transfer History"><SwapHoriz /></IconButton>
                                                {isMismatch && (
                                                    <IconButton color="warning" onClick={() => repairCustomer(customer)} title="Repair Customer Balances"><Build /></IconButton>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
                <TablePagination rowsPerPageOptions={[10]} component="div" count={filtered.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, newPage) => setPage(newPage)} />
            </Paper>

            {/* PAYMENT HISTORY DIALOG */}
            <Dialog open={openHistory} fullWidth maxWidth="md" onClose={() => setOpenHistory(false)}>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
                    Payment History - {selectedCustomer?.customerName}
                    <Button variant="contained" size="small" onClick={() => { setEditPaymentId(null); setPaymentForm({ amount: "", paymentMode: "", reference: "", note: "", date: new Date().toISOString().split("T")[0] }); setOpenPayment(true); }}>
                        Add Payment
                    </Button>
                </DialogTitle>
                <DialogContent>
                    <Table size="small">
                        <TableHead sx={{ background: "#f5f5f5" }}>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Reference</TableCell>
                                <TableCell>Note</TableCell>
                                <TableCell>Mode</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Entry Time</TableCell>
                                <TableCell align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {customerPayments.map(p => (
                                <TableRow key={p.id}>
                                    <TableCell>{p.date?.toDate ? moment(p.date.toDate()).format("DD/MM/YYYY") : ""}</TableCell>
                                    <TableCell>{p.reference || "-"}</TableCell>
                                    <TableCell>{p.note || "-"}</TableCell>
                                    <TableCell>{getPaymentMode(p.paymentMode)}</TableCell>
                                    <TableCell>{formatMoney(p.amount)}</TableCell>
                                    <TableCell>{p.entryTime?.toDate ? moment(p.entryTime.toDate()).format("DD/MM/YYYY hh:mm:ss A") : "-"}</TableCell>
                                    <TableCell align="center">
                                        <Box display="flex" gap={0.5} justifyContent="center">
                                            <IconButton color="primary" size="small" onClick={() => { setEditPaymentId(p.id); setPaymentForm({ amount: p.amount, paymentMode: p.paymentMode, reference: p.reference || "", note: p.note || "", date: p.date?.toDate().toISOString().split("T")[0] }); setOpenPayment(true); }} title="Edit Payment"><Edit fontSize="small" /></IconButton>
                                            <IconButton color="error" size="small" onClick={() => handleDeletePayment(p.id)} title="Delete Payment"><Delete fontSize="small" /></IconButton>
                                            {/* <IconButton color="warning" size="small" onClick={() => { setTransferFrom(selectedCustomer); setOpenTransfer(true); }} title="Transfer"><SwapHoriz fontSize="small" /></IconButton> */}
                                            {/* <IconButton color="warning" size="small" onClick={() => repairCustomer(selectedCustomer)} title="Repair"><Build fontSize="small" /></IconButton> */}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions><Button onClick={() => setOpenHistory(false)}>Close</Button></DialogActions>
            </Dialog>

            {/* ADD / EDIT PAYMENT */}
            <Dialog open={openPayment} maxWidth="sm" fullWidth>
                <DialogTitle>{editPaymentId ? "Edit Payment" : "Add Payment"}</DialogTitle>
                <DialogContent>
                    <TextField label="Date" type="date" fullWidth size="small" sx={{ mt: 2 }} InputLabelProps={{ shrink: true }} value={paymentForm.date} onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })} />
                    <TextField label="Amount" fullWidth size="small" sx={{ mt: 2 }} value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} />
                    <TextField select label="Payment Mode" fullWidth size="small" sx={{ mt: 2 }} value={paymentForm.paymentMode} onChange={(e) => setPaymentForm({ ...paymentForm, paymentMode: e.target.value })}>
                        {payModes.map(m => (<MenuItem key={m.id} value={m.payMode}>{m.payMode}</MenuItem>))}
                    </TextField>
                    <TextField label="Reference" fullWidth size="small" sx={{ mt: 2 }} value={paymentForm.reference} onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })} />
                    <TextField label="Note" fullWidth size="small" sx={{ mt: 2 }} value={paymentForm.note} onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPayment(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSavePayment}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* TRANSFER HISTORY DIALOG */}
            <Dialog open={openTransfer} onClose={() => !isTransferring && setOpenTransfer(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Transfer Account History</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" mb={2}>Transferring from: <b>{transferFrom?.customerName}</b></Typography>
                    <TextField select label="Select Target Customer" fullWidth size="small" value={transferToId} onChange={(e) => setTransferToId(e.target.value)} disabled={isTransferring}>
                        {customers.filter(c => c.id !== transferFrom?.id).map((m) => (
                            <MenuItem key={m.id} value={m.id}>{m.customerName}</MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenTransfer(false)} disabled={isTransferring}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={handleTransferHistory} disabled={isTransferring}>
                        {isTransferring ? "Processing..." : "Transfer Now"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* CUSTOMER EDIT DIALOG */}
            <Dialog open={openCustEdit} onClose={() => setOpenCustEdit(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Edit Customer Details</DialogTitle>
                <DialogContent>
                    <TextField label="Customer Name" fullWidth size="small" sx={{ mt: 2 }} value={custForm.customerName} onChange={(e) => setCustForm({ ...custForm, customerName: e.target.value })} />
                    <TextField label="Mobile Number" fullWidth size="small" sx={{ mt: 2 }} value={custForm.mobile} onChange={(e) => setCustForm({ ...custForm, mobile: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCustEdit(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpdateCustomer}>Update</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Customeracc;